import { useEffect, useState } from 'react'
import {
  CurecallButton,
  CurecallInput,
  CurecallModalContainer,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useNotification } from 'src/providers/notification.provider'
import { TwoFactorService } from 'src/services/two-factor.service'
import { TTotpSetupData } from 'src/types/two-factor'
import TwoFactorBackupCodes from './two-factor-backup-codes.component'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSetupComplete: () => void
}

type TSetupStep = 'qr-code' | 'confirm' | 'backup-codes'

export default function TwoFactorSetupTotp({ isOpen, onClose, onSetupComplete }: Props) {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const [step, setStep] = useState<TSetupStep>('qr-code')
  const [setupData, setSetupData] = useState<TTotpSetupData | null>(null)
  const [confirmCode, setConfirmCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && !setupData) {
      handleGenerateSetup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleGenerateSetup = async () => {
    setIsLoading(true)
    try {
      const data = await TwoFactorService.setupTotp()
      setSetupData(data)
    } catch {
      notify({
        title: t('common.actions.error'),
        message: t('pages.auth.account.two-factor.setup-error'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirm = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await TwoFactorService.confirmTotp(confirmCode.trim())
      setBackupCodes(result.backupCodes)
      setStep('backup-codes')
    } catch {
      setError(t('pages.auth.two-factor.error.invalid-code'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcknowledgeBackupCodes = () => {
    onSetupComplete()
    onClose()
  }

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('common.actions.close')}
      title={t('pages.auth.account.two-factor.setup-totp-title')}
      isOpen={isOpen}
      toggle={onClose}
      dialogClassName="sm:c-w-[500px]"
    >
      {step === 'qr-code' && setupData && (
        <div className="c-flex c-flex-col c-gap-6 c-items-center">
          <p className="c-text-sm c-text-gray-600 c-text-center">
            {t('pages.auth.account.two-factor.scan-qr-code')}
          </p>

          <img
            src={setupData.qrCodeDataUrl}
            alt={t('pages.auth.account.two-factor.qr-code-alt')}
            className="c-w-48 c-h-48"
          />

          <details className="c-w-full">
            <summary className="c-text-sm c-text-primary c-cursor-pointer">
              {t('pages.auth.account.two-factor.cant-scan')}
            </summary>
            <code className="c-block c-mt-2 c-p-3 c-bg-gray-50 c-rounded c-text-sm c-break-all c-text-center">
              {setupData.secret}
            </code>
          </details>

          <CurecallButton onClick={() => setStep('confirm')} className="c-w-full">
            {t('pages.auth.account.two-factor.next')}
          </CurecallButton>
        </div>
      )}

      {step === 'qr-code' && !setupData && (
        <div className="c-flex c-justify-center c-py-8">
          <CurecallButton isLoading={isLoading} onClick={handleGenerateSetup}>
            {t('pages.auth.account.two-factor.generate-qr')}
          </CurecallButton>
        </div>
      )}

      {step === 'confirm' && (
        <div className="c-flex c-flex-col c-gap-6">
          <p className="c-text-sm c-text-gray-600">{t('pages.auth.account.two-factor.enter-totp-code')}</p>

          <CurecallInput
            label={t('pages.auth.two-factor.code-label')}
            value={confirmCode}
            onChange={(e) => {
              setConfirmCode(e.target.value)
              setError(null)
            }}
            placeholder="000000"
            errorMessage={error ?? undefined}
            maxLength={6}
            autoFocus
          />

          <div className="c-flex c-justify-center c-gap-4">
            <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={() => setStep('qr-code')}>
              {t('common.actions.cancel')}
            </CurecallButton>
            <CurecallButton onClick={handleConfirm} isLoading={isLoading} isDisabled={confirmCode.length < 6}>
              {t('common.actions.confirm')}
            </CurecallButton>
          </div>
        </div>
      )}

      {step === 'backup-codes' && (
        <TwoFactorBackupCodes backupCodes={backupCodes} onAcknowledge={handleAcknowledgeBackupCodes} />
      )}
    </CurecallModalContainer>
  )
}
